import * as React from "react";
import { useMappedState } from "redux-react-hook";
import { Formik } from "formik";
import * as _ from "lodash";

import { FieldMultiSelect } from "src/library/components/Forms";
import {
  IDrop,
  useDropsApi
} from "src/core/drops/api/drops.api";
import type { IStore } from "src/core/shared/store";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";

interface DropMultiSelect {
  onDropsChange: (data: { selected: string, dropsSelected: string[]; fullDropsSelected: IDrop[]; }) => void;
  dropFilters?: FilterParams<IDrop>;
  dropPagination?: PaginatedParams<IDrop>,
  disabledOptions?: number[];
  isHidden?: (drop: IDrop) => boolean;
  debounceTime?: number
  singleSelection?: boolean;
  labelOverride?: string
}

const defaultPagination: PaginatedParams<IDrop> = {
  page: 0,
  limit: 100,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

export const DropMultiSelect: React.ReactFCWithChildren<DropMultiSelect> = ({
  onDropsChange, dropFilters, disabledOptions, isHidden, dropPagination, debounceTime, singleSelection, labelOverride

}) => {
  const [ recipientDropState, setRecipientDropState ] = React.useState<Array<{ text: string; value: string }>>([]);
  const [ loadedDrops, setLoadedDrops ] = React.useState<boolean>(false);
  const { terminology } = useMappedState(({ global }: IStore) => ({ terminology: global.settings && global.settings.terminology }));
  const [ dropsResponse, getDrops ] = useDropsApi("LIST_DROPS");
  const [ allDropsSearched, setAllDropsSearched ] = React.useState<IDrop[]>([]);

  // On mount, get the drops
  React.useEffect(() => {
    if (!loadedDrops) {
      getDrops({
        pagination: dropPagination || defaultPagination,
        filters: dropFilters || {}
      });
    }
  }, [
    getDrops,
    loadedDrops,
    dropFilters,
    dropPagination
  ]);

  //for passing into the multiSelect
  const handleSpecificSearch = React.useCallback((search: string | null) => {
    if (search) {
      //get fuzzy searched drops
      getDrops({
        pagination: {
          ...defaultPagination,
          limit: 50
        },
        //search for branch number and name
        filters: {
          ...dropFilters,
          $or: [ { name: { $like: `%${search}%` } }, { branchNumber: { $like: `%${search}%` } } ]
        }
      });
    } else {
      getDrops({
        pagination: dropPagination || defaultPagination,
        filters: dropFilters || {}
      });
    }
  }, [
    dropFilters,
    dropPagination,
    getDrops
  ]);

  // Set list of drops as drop options.
  React.useEffect(() => {
    if (dropsResponse.data && dropsResponse.data.result) {
      let drops = dropsResponse.data.result;

      if (isHidden) {
        drops = _.filter(drops, isHidden);
      }

      const dropValues = drops.map(drop => ({
        text: `(${drop.branchNumber}) ${drop.name}`,
        value: drop.id.toString()
      }));

      //populate data with all the new data ensuring uniqueness
      setRecipientDropState(prev => {
        const merged = [ ...prev, ...dropValues ];

        //hash maps do not allow duplicate keys
        return Array.from(new Map(merged.map(option => [ option.value, option ])).values());
      });

      //keeping tabs on all the drops we have found
      setAllDropsSearched(prev => {
        const merged = _.uniqBy([ ...prev, ...drops ], "id");

        return merged;
      });
      setLoadedDrops(true);
    }
  }, [ dropsResponse, isHidden ]);

  const label = React.useMemo(() => {
    if (!singleSelection) {
      return `Select ${terminology ? terminology.drop.plural.text : "Drops"}`;
    } else {
      return `Select ${terminology ? terminology.drop.singular.text : "Drop"}`;
    }
  }, [ singleSelection, terminology ]);

  return (
    <Formik
      initialValues={{ recipientMultipleDrops: undefined }}
      // dont submit as should be used in from above this
      onSubmit={() => null}
    >
      {({ values, handleChange }) => (
        <FieldMultiSelect
          values={values.recipientMultipleDrops}
          name="recipientMultipleDrops"
          handleChange={handleChange}
          label={labelOverride ? labelOverride : label}
          options={recipientDropState}
          disabledOptions={_.map(disabledOptions, d => d.toString())}
          validateOnChange={false}
          loading={dropsResponse.pending}
          fieldLogic={(affected, oldSelected) => onDropsChange({
            selected: affected,
            dropsSelected: oldSelected,
            fullDropsSelected: _.compact(_.map(oldSelected, selected => _.find(allDropsSearched, c => c.id.toString() === selected)))
          })}
          handleSpecificSearch={handleSpecificSearch}
          debounceTime={debounceTime}
        />
      )}
    </Formik>
  );
};
